// Bug 2026-07-07: gọi `message.success()` (tab "Tiên quyết" và mọi chỗ khác
// dùng static method của antd) chạy nhưng KHÔNG render gì ra DOM — root cause
// là antd v5 (`^5.23.0`) chỉ chính thức support React 16-18, project này dùng
// React 19 → cơ chế nội bộ antd dùng để mount static message/notification/Modal
// bị vỡ. Fix chính thức của antd: `@ant-design/v5-patch-for-react-19` — PHẢI
// import ở dòng ĐẦU TIÊN của entry file (trước bất kỳ import nào khác đụng tới
// antd), side-effect only (không cần gọi hàm gì).
import "@ant-design/v5-patch-for-react-19";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App as AntApp, ConfigProvider } from "antd";
// T5 (branch `techfest-demo`, KHÔNG merge main) — chuyển locale mặc định AntD
// sang enUS để các thành phần built-in (phân trang, DatePicker, nút OK/Cancel...)
// tự hiện tiếng Anh. Xem docs/demo/savina-techfest-brief-vong-3-dau-viec.md mục T5.
import enUS from "antd/locale/en_US";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";

import App from "@/App";
import { antdTheme } from "@/styles/tokens";
import { safeStringify } from "@/lib/safeStringify";

// ─── Sentry init (no-op khi DSN rỗng, giống pattern backend B4) ─────────────
// Sprint 11 B3 — thêm browserTracingIntegration tự auto-capture navigate
// breadcrumb (route change events) + fetch/xhr requests. Production debug
// visibility: dò "đường đi" user trước khi error → speedup incident triage.
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (dsn && dsn.trim().length > 0) {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.breadcrumbsIntegration({
        console: false, // tránh spam console.log breadcrumb
        dom: { serializeAttribute: ["aria-label", "data-testid"] },
        fetch: true,
        history: true, // navigate breadcrumb (route change)
        sentry: true,
        xhr: true,
      }),
    ],
  });
}

// Sprint 11 B3 — React Query global error handler + mutation success
// breadcrumb. Sentry capture mutation errors tự động kèm breadcrumb
// "form submit success/fail" — debug payment + form flow dễ hơn.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error, variables, _context) => {
        Sentry.addBreadcrumb({
          category: "mutation",
          message: "Mutation failed",
          level: "error",
          data: {
            error: String(error),
            // Sprint 27 D5 #17 — safeStringify: an toàn với undefined + circular + BigInt.
            variables: safeStringify(variables),
          },
        });
      },
      onSuccess: (_data, variables, _context) => {
        Sentry.addBreadcrumb({
          category: "mutation",
          message: "Mutation succeeded",
          level: "info",
          data: {
            variables: safeStringify(variables),
          },
        });
      },
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ConfigProvider locale={enUS} theme={antdTheme}>
      {/* antd v5 `App` — bổ sung cùng patch React 19 ở trên: giúp static
          `message.success()`/`message.error()` (dùng khắp admin UI, import
          trực tiếp từ "antd" thay vì hook `App.useApp()`) ăn đúng theme từ
          ConfigProvider (tránh warning "Static function can not consume
          context like dynamic theme"). KHÔNG cần đổi bất kỳ call site nào ở
          nơi khác trong codebase. */}
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  </StrictMode>,
);
